import { isAmp } from 'routes/BudgetModule/helpers/bind';
import _ from 'lodash';
import { getPhasesUpToIncludingCurrent } from 'routes/BudgetModule/shared/phases/helpers';
import {
    OPERATION_SOFTWARE_ID,
    SPLIT_BY,
    INSCRIPTION_EXPENSE_INCOME,
    INSCRIPTION_INVESTMENT_OPERATING,
    EXPENSE_INCOME_FIELD,
    SECTION_FIELD,
    REPORTED_FIELD,
    VOTED_TOTAL_FIELD,
} from 'routes/BudgetModule/config/constants';
import {
    BudgetExpenseOrIncome,
    BudgetPhases,
    BudgetSections,
} from 'business/constants';
import {
    ORDERED_LIST_METRIC,
    filterRejectedInscriptionOrImputation,
} from 'routes/BudgetModule/components/BudgetOverview/AllOperations/helpers/dataHelpers';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { isInscriptionBelongsToActiveBudgetProposal } from '../helpers/dataHelpers';
import { EXERCICE_METRICS } from 'routes/BudgetModule/config/queries';
import { sumMultiplePhaseAmounts } from 'routes/BudgetModule/helpers/dataOperation';
import { getClientBind } from 'store/APIClient';

const BALANCES_DIMENSIONS: Array<string> = [
    ...EXERCICE_METRICS,
    VOTED_TOTAL_FIELD,
    REPORTED_FIELD,
];

/** Array with all possible combination for expense income and section
 * ["EXPENSE INVESTMENT", "EXPENSE OPERATING", "INCOME INVESTMENT", "INCOME OPERATING",]
 */
export const EXPENSE_INCOME_AND_SECTION: Array<string> = Object.values(
    BudgetExpenseOrIncome,
).flatMap((d) => Object.values(BudgetSections).map((v) => `${d} ${v}`));

// @ts-expect-error [TS migration] Was not detected by flow
const addNumberValue = (objValue, srcValue) =>
    _.isNumber(objValue) ? objValue + srcValue : srcValue;

/** Calculate the total of the balance
 * Total is  Income - Expense
 */
const getTotal = (dataRow: ResponseDataT[]): ResponseDataT => {
    const expense = _.mergeWith(
        {},
        ...dataRow.filter((dr) =>
            // @ts-expect-error [TS migration] (previously $FlowFixMe) add array in response data
            dr.name.includes(BudgetExpenseOrIncome.EXPENSE),
        ),
        addNumberValue,
    );
    const income = _.mergeWith(
        {},
        ...dataRow.filter((dr) =>
            // @ts-expect-error [TS migration] (previously $FlowFixMe) add array in response data
            dr.name.includes(BudgetExpenseOrIncome.INCOME),
        ),
        addNumberValue,
    );
    const grandTotal = _.mergeWith({}, income, expense, _.subtract);
    grandTotal.name = 'TOTAL';
    return grandTotal;
};

// @ts-expect-error [TS migration] Was not detected by flow
const filterOnExpenseIncomeAndSection = (data, key) =>
    [data[EXPENSE_INCOME_FIELD], data[SECTION_FIELD]].every((el) =>
        key.includes(el),
    );

// @ts-expect-error [TS migration] Was not detected by flow
const filterInscriptionOnExpenseIncomeAndSection = (data, key) =>
    [
        data[INSCRIPTION_EXPENSE_INCOME],
        data[INSCRIPTION_INVESTMENT_OPERATING],
    ].every((el) => key.includes(el));

/** This function merge the budget lines and inscriptions data of an operation in an array of displayable data for the balance grid.
 * @param  {array} inscriptionsByBPAndOperation: List of inscriptions with for each the amounts, their section and if they belong to expense or income
 * Example: inscriptionsByBPAndOperation = {
 * "budgetLine.expenseIncome": "EXPENSE"
 * "budgetLine.investmentOperating": "INVESTMENT"
 * inscription: 1925
 * "primitiveBudget.amount": 100000
 * "primitiveBudget.status": "voted"
 * "serviceProposal.amount": 100000
 * "serviceProposal.status": "todo"
 * "simulatedArbitration.simulatedArbitratedAmount": 100000
 * "simulatedArbitration.status": "in_progress"
 * "supplementaryBudgetInscription.amount": 100000
 * "supplementaryBudgetInscription.status": "voted"
 * }
 * @param  {array} budgetLinesByBPAndOperation List of budget lines summed on the two previous year of the operation exercice
 * Example: budgetLinesByBPAndOperation = [{
 * "budgetLine.amountVoted..2020": 672804.1383
 * "budgetLine.amountVoted..2021": 515603.356
 * "budgetLine.expenseIncome": "EXPENSE"
 * "budgetLine.fullAmount..2020": 239013.2144
 * "budgetLine.fullAmount..2021": 511029.9956
 * "budgetLine.investmentOperating": "INVESTMENT"
 * "budgetLine.amountRealized..2020": 0
 * "budgetLine.amountRealized..2021": 0
 * }]
 * return:
 * operationsAggregated = [{
 *  name: "EXPENSE INVESTMENT"
 * "primitiveBudget.amount": 100000
 * "serviceProposal.amount": 100000
 * "simulatedArbitration.simulatedArbitratedAmount": 100000
 * "supplementaryBudgetInscription.amount": 100000
 * "budgetLine.amountVoted..2020": 672804.1383
 * "budgetLine.amountVoted..2021": 515603.356
 * "budgetLine.fullAmount..2020": 239013.2144
 * "budgetLine.fullAmount..2021": 511029.9956
 * "budgetLine.amountRealized..2020": 0
 * "budgetLine.amountRealized..2021": 0
 * }]
 */
export function getAmountsData(
    inscriptionsOfOperation: InscriptionT[],
    budgetLinesOfOperation: ResponseDataT[],
    budgetProposals: BudgetProposalT[],
    exercice: number,
): ResponseDataT[] {
    // @ts-expect-error [TS migration] Was not detected by flow
    const dataAggregated = [];

    // We generate an array with all the keys needed for each row.
    // There is the fields depending on the phase (service proposal, simulated arbitration..)
    const dataKeys = [
        ...ORDERED_LIST_METRIC,
        `${REPORTED_FIELD}${SPLIT_BY}${exercice}`,
    ] as string[];
    // And the fields depending on the exercice on the two last years (amount realized, ...)
    BALANCES_DIMENSIONS.forEach((bd) => {
        dataKeys.push(`${bd}${SPLIT_BY}${exercice - 1}`);
        dataKeys.push(`${bd}${SPLIT_BY}${exercice - 2}`);
    });

    // We iterate over each possible expenseOrIncome and section combination
    EXPENSE_INCOME_AND_SECTION.forEach((key) => {
        const dataRow = {};
        // @ts-expect-error [TS migration] Was not detected by flow
        dataRow.name = key;

        // The exercice amount's are given in the budget lines
        const exercicesData = budgetLinesOfOperation?.find((bl) =>
            filterOnExpenseIncomeAndSection(bl, key),
        );

        const earlierPhases = getPhasesUpToIncludingCurrent(
            isAmp(getClientBind())
                ? BudgetPhases.ARBITRAGE_DM
                : BudgetPhases.SUPPLEMENTARY_BUDGET,
        );
        // The phase amounts are given in the list of inscriptions
        // We sum them to have the total amount for each expenseOrIncome and section combination
        const phaseData = _.pick(
            sumMultiplePhaseAmounts(
                inscriptionsOfOperation
                    ?.filter((op) =>
                        filterInscriptionOnExpenseIncomeAndSection(op, key),
                    )
                    .filter((inscription) =>
                        isInscriptionBelongsToActiveBudgetProposal(
                            inscription,
                            budgetProposals,
                        ),
                    ),
                earlierPhases,
                'amount',
                OPERATION_SOFTWARE_ID,
                filterRejectedInscriptionOrImputation,
            )[0],
            ...ORDERED_LIST_METRIC,
            // TODO: Fixme - set better type
        ) as Record<string, string>;

        // It's possible that there is no amount in some case, for example if an operation has only expenses and no incomes.
        // In this case, the query doesn't return any amounts for the incomes.
        // In order to display it, we force the populate of the row
        dataKeys.forEach((dk) => {
            if (exercicesData && Object.keys(exercicesData).includes(dk)) {
                // @ts-expect-error [TS migration] Was not detected by flow
                dataRow[dk] = exercicesData[dk];
            } else if (phaseData && Object.keys(phaseData).includes(dk)) {
                // @ts-expect-error [TS migration] Was not detected by flow
                dataRow[dk] = phaseData[dk];
            } else {
                // @ts-expect-error [TS migration] Was not detected by flow
                dataRow[dk] = 0;
            }
        });
        dataAggregated.push(dataRow);
    });
    // @ts-expect-error [TS migration] Was not detected by flow
    dataAggregated.push(getTotal(dataAggregated));
    return dataAggregated;
}
